import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../contexts/LanguageContext';
import { ShoppingCart, TrendingUp, BarChart3, Award, Palette, Briefcase } from 'lucide-react';
import ServiceCard from './services/ServiceCard';

const HEADER_INITIAL = { opacity: 0, y: 30 };
const HEADER_VISIBLE = { opacity: 1, y: 0 };
const HEADER_TRANSITION = { duration: 0.6 };

const BADGE_INITIAL = { opacity: 0, scale: 0.8 };
const BADGE_VISIBLE = { opacity: 1, scale: 1 };
const BADGE_TRANSITION = { duration: 0.5 };

const buildServices = (t) => [
  { id: 'ecommerce', icon: ShoppingCart, title: t.services.ecommerce.title, description: t.services.ecommerce.description, gradient: 'from-blue-500 to-cyan-500' },
  { id: 'performance', icon: TrendingUp, title: t.services.performance.title, description: t.services.performance.description, gradient: 'from-orange-500 to-red-500' },
  { id: 'analysis', icon: BarChart3, title: t.services.analysis.title, description: t.services.analysis.description, gradient: 'from-purple-500 to-pink-500' },
  { id: 'branding', icon: Award, title: t.services.branding.title, description: t.services.branding.description, gradient: 'from-green-500 to-emerald-500' },
  { id: 'design', icon: Palette, title: t.services.design.title, description: t.services.design.description, gradient: 'from-yellow-500 to-orange-500' },
  { id: 'business', icon: Briefcase, title: t.services.business.title, description: t.services.business.description, gradient: 'from-indigo-500 to-blue-500' },
];

const Services = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const services = buildServices(t);

  return (
    <section id="services" className="py-24 bg-slate-900 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.05),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(168,85,247,0.05),transparent_50%)]" />

      <div ref={ref} className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={HEADER_INITIAL}
          animate={isInView ? HEADER_VISIBLE : HEADER_INITIAL}
          transition={HEADER_TRANSITION}
          className="text-center mb-16"
        >
          <motion.div
            initial={BADGE_INITIAL}
            animate={isInView ? BADGE_VISIBLE : BADGE_INITIAL}
            transition={BADGE_TRANSITION}
            className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-orange-500/20 to-blue-500/20 border border-orange-500/30 mb-4"
          >
            <span className="text-orange-400 font-semibold">{t.services.subtitle}</span>
          </motion.div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-gradient-to-r from-white via-blue-100 to-white bg-clip-text text-transparent">
              {t.services.title}
            </span>
          </h2>
        </motion.div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, idx) => (
            <ServiceCard key={service.id} service={service} idx={idx} isInView={isInView} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services;
